import { Preferences } from '@capacitor/preferences';

export interface IosSignificantUpdateConfig {
  /**
   * Bump this only when a release is a legally significant update that needs
   * a fresh adult acknowledgment or parent/guardian approval.
   */
  id: string;
  /**
   * Flip this to true only for builds/releases that legal review treats as a
   * significant update under applicable app-store age assurance rules.
   */
  isSignificantUpdate: boolean;
  /**
   * Keep this concise and user-facing. Apple presents it in the system
   * acknowledgment/approval flows when isSignificantUpdate is true.
   */
  descriptionTemplate: string;
}

export const iosSignificantUpdateConfig: IosSignificantUpdateConfig = {
  id: 'ios-significant-update-2026-04-v1',
  isSignificantUpdate: false,
  descriptionTemplate: '{appName} includes an update that may require parent or guardian approval before access continues.',
};

export const iosSignificantUpdateId = iosSignificantUpdateConfig.id;

export const isIosSignificantUpdateEnabled = () => (
  iosSignificantUpdateConfig.isSignificantUpdate
);

const getNormalizedAppName = (appName: string) => (
  appName.toLowerCase().replace(/[^a-z0-9]+/g, '-')
);

const getAdultAcknowledgmentStorageKey = (appName: string) => (
  `ios_significant_update_adult_ack_${getNormalizedAppName(appName)}_${iosSignificantUpdateId}`
);

export const getIosSignificantUpdateDescription = (appName: string) => (
  iosSignificantUpdateConfig.descriptionTemplate.replace(/\{appName\}/g, appName)
);

export const hasStoredIosAdultAcknowledgment = async (appName: string) => {
  const response = await Preferences.get({
    key: getAdultAcknowledgmentStorageKey(appName),
  });

  return Boolean(response.value);
};

export const setStoredIosAdultAcknowledgment = async (appName: string) => {
  await Preferences.set({
    key: getAdultAcknowledgmentStorageKey(appName),
    value: new Date().toISOString(),
  });
};

export const clearStoredIosAdultAcknowledgment = async (appName: string) => {
  await Preferences.remove({
    key: getAdultAcknowledgmentStorageKey(appName),
  });
};
